import React from "react";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core"
import D3Tooltip from "../D3Charts/D3Tooltip";
import { useScreenSize } from "customHooks/useScreenSize";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
    gridContaniner: {
        gridGap: theme.spacing(1),
        padding: `0px ${theme.spacing(6.25)}px ${theme.spacing(1.25)}px ${theme.spacing(3.75)}px`,
    },
    noDataContainer: {
        justifyContent: "center",
        alignItems: "center",
        height: "80%"
    },
    gridItem: (props) => ({
        width: props?.width || "100%",
        // minWidth: "150px",
        border: `${theme.spacing(0.25)}px solid ${props?.color || theme.palette.primary.main}`,
        borderTop: "none",
        borderRadius: theme.spacing(1.875),
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        }
    }),
    processBox: (props) => ({
        width: "100%",
        height: theme.spacing(12.5),
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: theme.spacing(1.875),
        position: "relative",
        backgroundColor: props?.color || "transparent",
        marginRight: theme.spacing(1.25),
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        padding: `${theme.spacing(1.25)}px 0px ${theme.spacing(1.25)}px ${theme.spacing(5.625)}px`,
        [theme.breakpoints.down("sm")]: {
            height: theme.spacing(6.25)
        }
    }),
    processBoxArrow: (props) => ({
        borderLeft: `${theme.spacing(5)}px solid transparent`,
        borderTop: `${theme.spacing(5)}px solid ${props?.color || theme.palette.primary.main}`,
        borderBottom: `${theme.spacing(5)}px solid transparent`,
        borderRight: `${theme.spacing(5)}px solid ${props?.color || theme.palette.primary.main}`,
        borderRadius: theme.spacing(1.875),
        transform: "rotate(45deg)",
        position: "absolute",
        right: `-${theme.spacing(5)}px`,
        top: theme.spacing(1.25),
        zIndex: 1,
        boxShadow: `1px -1px 0px 0px ${theme.palette.black}`,
        [theme.breakpoints.down("sm")]: {
            borderLeft: `${theme.spacing(2.5)}px solid transparent`,
            borderTop: `${theme.spacing(2.5)}px solid ${props?.color || theme.palette.primary.main}`,
            borderBottom: `${theme.spacing(2.5)}px solid transparent`,
            borderRight: `${theme.spacing(2.5)}px solid ${props?.color || theme.palette.primary.main}`,
            borderRadius: theme.spacing(1),
            right: `-${theme.spacing(2.5)}px`,
            top: `${theme.spacing(0.6)}px`,
        }
    }),
    labelBox: {
        textAlign: "center",
        position: "relative",
        width: "90%",
        left: "5%",
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
        [theme.breakpoints.down("sm")]: {
            display: "inline-block",
            width: "45%",
            float: "left",
            borderBottom: 'none',
            textAlign: "left",
            '&:last-child': {
                textAlign: 'right',
            },
        }
    },
    cardLabel: {
        textAlign: "left",
        paddingTop: theme.spacing(1.25),
        [theme.breakpoints.down("sm")]: {
            display: "inline"
        }
    },
    cardValue: {
        textAlign: "center",
        fontSize: theme.spacing(3),
        paddingBottom: theme.spacing(1.25),
        [theme.breakpoints.down("sm")]: {
            display: "inline",
        }
    },
}))

export const ProcessBox = (props) => {
    const { state, totalCount, totalRatio, colorPalette, itemWidth, testId } = props;
    const classes = useStyles({ color: colorPalette, width: itemWidth });
    const screenSize = useScreenSize();
    const { t } = useTranslation()
    const charLimits = { sm: 70, md: 5, lg: 30, xl: 30 }

    return <Grid item className={classes.gridItem} >
        <D3Tooltip title={< Typography>{t(state)}</Typography>}>
            <Box className={classes.processBox}>
                <Typography data-testid={`${testId}-title-${state}`}>
                    {t(state)?.length > charLimits[screenSize] ? t(state).substring(0, charLimits[screenSize]) + '...' : t(state)}
                </Typography>
                <div className={classes.processBoxArrow}></div>
            </Box>
        </D3Tooltip>
        <Box className={classes.labelBox}>
            <Typography className={classes.cardLabel} component="div">{t('qty')}: </Typography>
            <Typography className={classes.cardValue} component="div" data-testid={`${testId}-qty-${state}`}>{totalCount}</Typography>
        </Box>
        <Box className={classes.labelBox}>
            <Typography className={classes.cardLabel} component="div">{t('p%')}:</Typography>
            <Typography className={classes.cardValue} component="div" data-testid={`${testId}-percentage-${state}`}>{totalRatio}%</Typography>
        </Box>
    </Grid>
}

export const LaoProcessChart = (props) => {
    const { loading, data, testId, colorPalette } = props;
    const classes = useStyles();
    const { t } = useTranslation();

    const itemWidth = `calc(${100 / data?.length}% - 8px)`;
    const noDataText = loading ? "loading" : "no_data_found";

    return loading || !data?.length ? <Grid container className={classes.noDataContainer}><Typography data-testid={noDataText}> {t(noDataText)} </Typography></Grid>
        : <Grid container row className={classes.gridContaniner} data-testid={testId}>
            {data.map(v => {
                return <ProcessBox {...v} testId={testId} colorPalette={colorPalette} itemWidth={itemWidth} key={v.label} />
            })}
        </Grid>
}